// Cognito sign-in for the SPA: OAuth2 authorization code + PKCE against the managed-login
// domain, then the ID token is exchanged for a CertBuddy session at /api/auth/cognito.
import { API_BASE_URL } from "../config";

const env = (key) => (window._env_ && window._env_[key]) || process.env[key] || "";

export const cognitoConfig = () => ({
  domain: env("REACT_APP_COGNITO_DOMAIN").replace(/\/$/, ""),
  clientId: env("REACT_APP_COGNITO_CLIENT_ID"),
  mode: (env("REACT_APP_AUTH_MODE") || "both").toLowerCase(),
});

export const ssoEnabled = () => {
  const c = cognitoConfig();
  return Boolean(c.domain && c.clientId);
};

export const ssoOnly = () => ssoEnabled() && cognitoConfig().mode === "cognito";

export const redirectUri = () => `${window.location.origin}/auth/callback`;

const b64url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const random = (n) => b64url(crypto.getRandomValues(new Uint8Array(n)));

const sha256 = async (text) =>
  b64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text)));

export const buildAuthorizeUrl = ({
  domain,
  clientId,
  redirectUri,
  challenge,
  state,
  identityProvider,
}) => {
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: redirectUri,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  });
  if (identityProvider) {
    params.set("identity_provider", identityProvider);
  }
  return `${domain}/oauth2/authorize?${params.toString()}`;
};

export const startSso = async (identityProvider) => {
  const { domain, clientId } = cognitoConfig();
  const verifier = random(32);
  const state = random(16);
  sessionStorage.setItem("sso:verifier", verifier);
  sessionStorage.setItem("sso:state", state);
  window.location.assign(
    buildAuthorizeUrl({
      domain,
      clientId,
      redirectUri: redirectUri(),
      challenge: await sha256(verifier),
      state,
      identityProvider,
    })
  );
};

const finishSsoOnce = async (search) => {
  const params = new URLSearchParams(search);
  const code = params.get("code");
  const state = params.get("state");
  const expected = sessionStorage.getItem("sso:state");
  const verifier = sessionStorage.getItem("sso:verifier");
  sessionStorage.removeItem("sso:state");
  sessionStorage.removeItem("sso:verifier");
  if (!code || !state || state !== expected || !verifier) {
    throw new Error("Sign-in state did not match; please try again");
  }
  const { domain, clientId } = cognitoConfig();
  const tokenRes = await fetch(`${domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: clientId,
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }).toString(),
  });
  if (!tokenRes.ok) {
    throw new Error("Sign-in could not be completed");
  }
  const { id_token: idToken } = await tokenRes.json();
  const apiRes = await fetch(`${API_BASE_URL}/api/auth/cognito`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token: idToken }),
  });
  const body = await apiRes.json().catch(() => ({}));
  if (!apiRes.ok) {
    throw new Error(body.detail || "Sign-in was refused");
  }
  return { ...body, tokenIssuedAt: Date.now(), ssoUsed: true };
};

// One exchange per callback URL. The state and verifier are single-use, and React StrictMode
// (development) mounts the callback effect twice; the second call must join the first exchange
// rather than find the consumed state and report a mismatch.
const exchanges = new Map();

export const finishSso = (search) => {
  if (!exchanges.has(search)) {
    exchanges.set(search, finishSsoOnce(search));
  }
  return exchanges.get(search);
};

export const cognitoLogoutUrl = () => {
  const { domain, clientId } = cognitoConfig();
  const params = new URLSearchParams({
    client_id: clientId,
    logout_uri: `${window.location.origin}/login`,
  });
  return `${domain}/logout?${params.toString()}`;
};
